/**
 * Date formatting utilities
 */
import { formatDistanceToNow } from 'date-fns';

/**
 * Format a date string or Date object for display
 * @param {string|Date} dateTime - Date to format
 * @returns {string} - Formatted date
 */
export const formatDateTimeForDisplay = (dateTime) => {
  if (!dateTime) return 'N/A';
  
  try {
    const date = new Date(dateTime);
    
    // Format: "Jan 25, 2023, 3:30 PM"
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(dateTime);
  }
};

/**
 * Get time elapsed since a date
 * @param {string|Date} dateTime - Start date
 * @returns {string} - Human readable time elapsed
 */
export const getTimeElapsedSince = (dateTime) => {
  if (!dateTime) return '';
  
  try {
    const date = new Date(dateTime);
    const now = new Date();
    const diffMs = now - date;
    
    // Convert to appropriate units
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.error('Error calculating elapsed time:', error);
    return '';
  }
};

/**
 * Get duration between two dates in human readable format
 * @param {string|Date} startDateTime - Start date/time
 * @param {string|Date} endDateTime - End date/time
 * @returns {string} - Duration string
 */
export const getDuration = (startDateTime, endDateTime) => {
  if (!startDateTime || !endDateTime) return 'N/A';
  
  try {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const diffMs = end - start;
    
    if (diffMs < 0) return 'Invalid duration';
    
    // Convert to appropriate units
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    const remainingSecs = diffSecs % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m ${remainingSecs}s`;
    } else if (diffMins > 0) {
      return `${diffMins}m ${remainingSecs}s`;
    } else {
      return `${diffSecs}s`;
    }
  } catch (error) {
    console.error('Error calculating duration:', error);
    return 'N/A';
  }
};

/**
 * Format sync status with appropriate styling information
 * @param {Object} lastSync - Last sync information
 * @returns {Object} Formatted sync status with display properties
 */
export const formatSyncStatus = (lastSync) => {
  if (!lastSync) {
    return {
      text: 'Never synced',
      icon: 'bi-question-circle',
      className: 'text-muted',
      timeAgo: null
    };
  }

  const timeAgo = formatDistanceToNow(new Date(lastSync.startTime), { addSuffix: true });
  let statusClass = 'text-muted';
  let statusIcon = 'bi-info-circle';
  let statusText = lastSync.status;

  switch (lastSync.status) {
    case 'running':
      statusClass = 'text-primary';
      statusIcon = 'bi-hourglass-split';
      statusText = 'In Progress';
      break;
    case 'completed':
      statusClass = 'text-success';
      statusIcon = 'bi-check-circle-fill';
      statusText = 'Completed';
      break;
    case 'completed_with_errors':
      statusClass = 'text-warning';
      statusIcon = 'bi-exclamation-triangle-fill';
      statusText = 'Completed with Errors';
      break;
    case 'failed':
      statusClass = 'text-danger';
      statusIcon = 'bi-x-octagon-fill';
      statusText = 'Failed';
      break;
    default:
      statusIcon = 'bi-question-circle';
      statusText = 'Unknown';
  }

  return {
    text: statusText,
    icon: statusIcon,
    className: statusClass,
    timeAgo: timeAgo,
    fullText: `${statusText} (${timeAgo})`
  };
};

/**
 * Format sync duration in human readable format
 * @param {number} duration - Duration in seconds
 * @returns {string} Formatted duration
 */
export const formatSyncDuration = (duration) => {
  if (!duration || duration < 0) return 'N/A';
  
  if (duration < 60) {
    return `${Math.round(duration)}s`;
  } else if (duration < 3600) {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.round(duration % 60);
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

/**
 * Get sync statistics summary from last sync results
 * @param {Object} lastSync - Last sync information
 * @returns {Object} Summary statistics
 */
export const getSyncStatsSummary = (lastSync) => {
  if (!lastSync || !lastSync.results) {
    return {
      total: 0,
      successful: 0,
      failed: 0,
      created: 0,
      updated: 0
    };
  }

  const results = lastSync.results;
  
  // Handle different result structures from different sync types
  const stats = {
    total: results.total || results.totalRecords || 0,
    successful: results.successful || results.created + results.updated || 0,
    failed: results.failed || 0,
    created: results.created || 0,
    updated: results.updated || 0
  };

  // Handle specific sync type result structures
  if (results.stats) {
    stats.total = results.stats.total || stats.total;
    stats.successful = results.stats.successful || stats.successful;
    stats.failed = results.stats.failed || stats.failed;
    stats.created = results.stats.created || stats.created;
    stats.updated = results.stats.updated || stats.updated;
  }

  return stats;
}; 